import { useState } from 'react'
import { AlertCircle, Globe2 } from 'lucide-react'
import { RoleGate } from '@/components/auth/RoleGate'
import { AddressDisplay } from '@/components/data/AddressDisplay'
import { EmptyState } from '@/components/data/EmptyState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { ApiError } from '@/lib/api'
import { countryName } from '@/lib/countries'
import { formatTimestamp } from '@/lib/utils'
import type { OperatorRole } from '@/types/api'
import type {
    JurisdictionOverrideAddRequest,
    JurisdictionOverrideLiftRequest,
    JurisdictionOverrideListWire,
    JurisdictionOverrideWire,
} from '@/types/jurisdictions'
import { AddOverrideDialog } from './AddOverrideDialog'
import { LiftOverrideDialog } from './LiftOverrideDialog'
import { ageInDays, MATRIX_CHANGE_DUE_DAYS, TRIPWIRE_LABELS } from './overrideCopy'

// The service lets admin through every MLRO route, so the console does too.
const WRITE_ROLES: OperatorRole[] = ['mlro', 'admin']

interface Props {
    data: JurisdictionOverrideListWire | undefined
    isLoading: boolean
    error: unknown
    onAdd: (body: JurisdictionOverrideAddRequest) => Promise<unknown>
    onLift: (id: string, body: JurisdictionOverrideLiftRequest) => Promise<unknown>
    // Injected so the 14-day badge is deterministic in stories and tests.
    now?: Date
}

export function JurisdictionOverridesView({
    data,
    isLoading,
    error,
    onAdd,
    onLift,
    now = new Date(),
}: Props) {
    const [addOpen, setAddOpen] = useState(false)
    const [lifting, setLifting] = useState<JurisdictionOverrideWire | null>(null)

    if (isLoading) {
        return (
            <div className="space-y-4" aria-busy="true" aria-label="Loading provisional blocks">
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-24 w-full" />
            </div>
        )
    }

    if (error || !data) {
        const expired = error instanceof ApiError && error.status === 401
        return (
            <EmptyState
                icon={AlertCircle}
                title={expired ? 'Session expired' : 'Could not load provisional blocks'}
                description={
                    expired
                        ? 'Sign in again to see the provisional blocks.'
                        : `${error instanceof Error ? error.message : 'The service did not answer.'} Reload the page to try again; if it keeps failing, check the service status on the dashboard.`
                }
            />
        )
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                    <div className="space-y-1.5">
                        <CardTitle>Active provisional blocks</CardTitle>
                        <CardDescription>
                            Refused at registration, entry and waitlist signup, on top of the
                            jurisdiction matrix. A block {MATRIX_CHANGE_DUE_DAYS} days old or more
                            should become a matrix change.
                        </CardDescription>
                    </div>
                    <RoleGate roles={WRITE_ROLES}>
                        <Button size="sm" variant="destructive" onClick={() => setAddOpen(true)}>
                            Block country
                        </Button>
                    </RoleGate>
                </CardHeader>
                <CardContent>
                    {data.active.length === 0 ? (
                        <EmptyState
                            icon={Globe2}
                            title="No provisional blocks"
                            description="Countries the jurisdiction matrix blocks are refused already."
                        />
                    ) : (
                        <Table aria-label="Active provisional blocks">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Country</TableHead>
                                    <TableHead>Tripwire</TableHead>
                                    <TableHead>Note</TableHead>
                                    <TableHead>Added by</TableHead>
                                    <TableHead>Added</TableHead>
                                    <TableHead>Age</TableHead>
                                    <TableHead>
                                        <span className="sr-only">Actions</span>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.active.map((o) => {
                                    const age = ageInDays(o.created_at, now)
                                    return (
                                        <TableRow key={o.id}>
                                            <TableCell>
                                                <CountryCell code={o.country_code} />
                                            </TableCell>
                                            <TableCell title={TRIPWIRE_LABELS[o.tripwire]}>
                                                {o.tripwire}
                                            </TableCell>
                                            <TableCell className="max-w-xs whitespace-normal">
                                                {o.note}
                                            </TableCell>
                                            <TableCell>
                                                <AddressDisplay address={o.created_by} />
                                            </TableCell>
                                            <TableCell>{formatTimestamp(o.created_at)}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <span>
                                                        {age} {age === 1 ? 'day' : 'days'}
                                                    </span>
                                                    {age >= MATRIX_CHANGE_DUE_DAYS && (
                                                        <Badge variant="warning">
                                                            Matrix change due
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <RoleGate roles={WRITE_ROLES}>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setLifting(o)}
                                                        aria-label={`Lift block on ${countryName(o.country_code)}`}
                                                    >
                                                        Lift block
                                                    </Button>
                                                </RoleGate>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Lifted blocks</CardTitle>
                    <CardDescription>The 50 most recent, newest first.</CardDescription>
                </CardHeader>
                <CardContent>
                    {data.lifted.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            No block has been lifted yet.
                        </p>
                    ) : (
                        <Table aria-label="Lifted blocks">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Country</TableHead>
                                    <TableHead>Tripwire</TableHead>
                                    <TableHead>Note</TableHead>
                                    <TableHead>Added</TableHead>
                                    <TableHead>Lifted by</TableHead>
                                    <TableHead>Lifted</TableHead>
                                    <TableHead>Lift note</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.lifted.map((o) => (
                                    <TableRow key={o.id}>
                                        <TableCell>
                                            <CountryCell code={o.country_code} />
                                        </TableCell>
                                        <TableCell title={TRIPWIRE_LABELS[o.tripwire]}>
                                            {o.tripwire}
                                        </TableCell>
                                        <TableCell className="max-w-xs whitespace-normal">
                                            {o.note}
                                        </TableCell>
                                        <TableCell>{formatTimestamp(o.created_at)}</TableCell>
                                        <TableCell>
                                            <AddressDisplay address={o.lifted_by ?? undefined} />
                                        </TableCell>
                                        <TableCell>{formatTimestamp(o.lifted_at)}</TableCell>
                                        <TableCell className="max-w-xs whitespace-normal">
                                            {o.lift_note ?? '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <AddOverrideDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                matrixBlocked={data.matrix_blocked}
                activeCountries={data.active.map((o) => o.country_code)}
                onAdd={onAdd}
            />
            <LiftOverrideDialog
                override={lifting}
                onOpenChange={(open) => {
                    if (!open) setLifting(null)
                }}
                onLift={onLift}
            />
        </div>
    )
}

function CountryCell({ code }: { code: string }) {
    return (
        <span>
            {countryName(code)}{' '}
            <span className="text-muted-foreground font-mono text-xs">{code}</span>
        </span>
    )
}
